import { useEffect, useState } from "react";
import useWebSocket from "react-use-websocket";
import { useAuth } from "@clerk/clerk-react";
import { api } from "./api";

/**
 * Log level enumeration
 */
export type LogLevel = "debug" | "info" | "warning" | "error" | "fatal";

/**
 * Sensor log entry
 */
export interface SensorLog {
  /** Unique identifier for the log entry */
  id: string;
  /** Sensor ID that generated the log */
  sensor_id: string;
  /** Log level */
  level: LogLevel;
  /** Log message content */
  message: string;
  /** Timestamp when the log was generated */
  timestamp: string; // ISO 8601 format
  /** Additional metadata (optional) */
  metadata?: Record<string, unknown>;
}

/**
 * Websocket message types for sensor logs
 */

/**
 * Hook for connecting to sensor logs websocket
 * Only connects when sensorId is provided
 */
export const useSensorLogs = (sensorId?: string) => {
  const [logs, setLogs] = useState<SensorLog[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const { sessionId, getToken } = useAuth();

  const socketUrlBase = api.defaults.baseURL
    ?.replace(/^http/, "ws")
    .replace(/^https/, "wss");
  const socketUrl = socketUrlBase + "/v0/realtime/logs";

  // Fetch token asynchronously
  useEffect(() => {
    const fetchToken = async () => {
      if (!sessionId) {
        setToken(null);
        return;
      }
      try {
        const freshToken = await getToken();
        setToken(freshToken);
      } catch {
        setToken(null);
      }
    };

    fetchToken();
  }, [sessionId, getToken]);

  // Only connect if we have a sensorId, session, and token
  const shouldConnect = !!sensorId && !!sessionId && !!token;

  // Build the websocket URL with token and sensor_id
  const apiUrl = new URL(socketUrl);
  if (token) {
    apiUrl.searchParams.set("token", token);
  }
  if (sensorId) {
    apiUrl.searchParams.set("sensor_id", sensorId);
  }

  const { readyState } = useWebSocket(apiUrl.toString(), {
    shouldReconnect: () => shouldConnect,
    retryOnError: true,
    heartbeat: true,
    reconnectAttempts: Infinity,
    reconnectInterval: 5000,
    onOpen: () => {},
    onClose: () => {},
    onMessage: (message) => {
      try {
        const log = JSON.parse(message.data) as SensorLog;
        if (log.sensor_id === sensorId) {
          setLogs((prev) => {
            const newLogs = [log, ...prev.slice(0, 999)];
            return newLogs;
          });
        }
      } catch {
        return; 
      }
    },
    onError: () => {
      return;
    },
  });

  // Clear logs when sensorId changes
  useEffect(() => {
    setLogs([]);
  }, [sensorId]);

  const clearLogs = () => {
    setLogs([]);
  };

  return {
    logs,
    clearLogs,
    isConnected: readyState === 1, // WebSocket.OPEN
    readyState,
    isLoading: !token && !!sessionId, // Loading while fetching token
  };
};
